import { BidBattleCard } from "@/components/battle";
import { Button } from "@bidbattles/bloks/components/ui/button";
import { Link } from "@bidbattles/bloks/components/ui/link";
import { PageLoader } from "@bidbattles/bloks/components/ui/loading";
import { BBBackendInfiniteQueryOptions } from "@bidbattles/bloks/lib/rapace";
import { cn } from "@bidbattles/bloks/lib/ui";
import { useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowDown } from "@untitledui/icons";
import { startOfDay } from "date-fns";
import { groupBy } from "es-toolkit";
import { useInView } from "motion/react";
import { useEffect, useMemo, useRef } from "react";

export const Route = createFileRoute("/history")({
	async loader({ context }) {
		await context.queryClient.ensureInfiniteQueryData(
			BBBackendInfiniteQueryOptions.listBattles({ limit: 12, completed: true }),
		);
	},
	component: RouteComponent,
});

function RouteComponent() {
	const {
		data: { pages },
		hasNextPage,
		fetchNextPage,
		isFetchingNextPage,
	} = useSuspenseInfiniteQuery({
		...BBBackendInfiniteQueryOptions.listBattles({
			limit: 12,
			completed: true,
		}),
	});
	const battles = useMemo(() => {
		const flattened = pages.flatMap((page) => page.battles);
		return flattened;
	}, [pages]);

	const ref = useRef(null);
	const inView = useInView(ref);
	useEffect(() => {
		if (inView && hasNextPage && !isFetchingNextPage) {
			fetchNextPage();
		}
	}, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

	return (
		<main className="flex flex-col gap-y-8 items-stretch grow h-full">
			<section className="p-box flex flex-col items-center gap-y-12 md:gap-y-16 mx-auto">
				<div className="flex flex-col gap-y-3 text-center max-w-100 md:max-w-180">
					<h1 className="text-gray-900 font-bold text-5xl leading-11 md:text-6xl md:leading-22.5">
						BidBattles History
					</h1>
					<p className="text-gray-500 text-lg leading-7">
						Master the art of strategic bidding and win premium products at
						unbeatable prices
					</p>
				</div>
			</section>
			<section className="flex flex-col items-center bg-gray-warm-100 gap-12 py-box">
				<div className="w-full p-box max-w-container flex flex-wrap gap-8 mb-6 justify-center">
					{battles.map((battle) => (
						<BidBattleCard
							key={battle.id}
							className={cn("basis-1/1 sm:basis-1/3 md:basis-1/4 md:max-w-70")}
							battle={battle}
							render={(props) => (
								<Link
									to="/battle/$id/details"
									params={{ id: battle.id }}
									{...props}
								/>
							)}
						/>
					))}
				</div>
				<div
					ref={ref}
					className="flex justify-center items-center gap-4 w-full"
				>
					{battles.length > 0 &&
						(isFetchingNextPage ? (
							<PageLoader />
						) : (
							<Button
								variant="white"
								disabled={!hasNextPage || isFetchingNextPage}
								onClick={() => fetchNextPage()}
							>
								<ArrowDown size="1em" />
								See more
							</Button>
						))}
				</div>
			</section>
		</main>
	);
}
